"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-1">
        {[1, 2, 3, 4, 5].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative h-9 px-4 py-2 text-sm font-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] after:ease-[var(--ease-spring)] hover:after:w-full focus:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" disabled>
        <Menu className="h-6 w-6" />
      </Button>;
  }
  return <>
      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {isOpen && <div className="absolute top-full left-0 w-full bg-background border-t shadow-medium lg:hidden">
          <nav className="flex flex-col p-4">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-4 py-3 text-sm font-medium transition-colors duration-[var(--duration-fast)] hover:bg-accent rounded-sm" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
            <Button className="mt-4 w-full hover:!bg-accent-hover focus:!bg-accent-active hover:-translate-y-0.5 active:translate-y-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]" asChild>
              <Link to="/#lets-talk">Book Now</Link>
            </Button>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isScrolled ? "h-16 shadow-medium" : "h-24"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
          <img src="https://denverweddingco.com/wp-content/uploads/2026/01/denver_wedding_co_logo.png" alt="Jordan Ebner" className="h-7" />
        </Link>

        <div className="flex items-center gap-6">
          <MenuProvider menu_id="3" className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="hidden lg:flex items-center gap-4">
            <div className="relative">
              <Button variant="ghost" size="icon" className="hover:!bg-transparent focus:!bg-transparent transition-opacity duration-[var(--duration-fast)]" onClick={() => setIsSearchOpen(!isSearchOpen)} aria-label="Toggle search">
                <Search className="h-5 w-5" />
              </Button>
              {isSearchOpen && <div className="absolute right-0 top-full mt-2 w-64 animate-fade-in-up">
                  <Input type="search" placeholder="Search..." className="w-full" autoFocus onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)} />
                </div>}
            </div>

            <Button className="hover:!bg-accent-hover focus:!bg-accent-active hover:-translate-y-0.5 active:translate-y-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] shadow-soft hover:shadow-medium border border-accent-dark/20" asChild>
              <Link to="/#lets-talk">Book Now</Link>
            </Button>
          </div>

          <MenuProvider menu_id="3" className="lg:hidden">
            <MobileMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}